import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Flame, Leaf, Clock, Star, Minus } from "lucide-react";
import { MenuItem, CartItem } from "@/types";
import { FoodCustomizationModal } from "./FoodCustomizationModal";
import { getMenuItemImageUrl } from "@/services/utlits";
import { useApp } from "@/App";
import { useRestaurantStatus } from "@/hooks/useRestaurantStatus";

interface MenuItemCardProps {
  item: MenuItem;
  onAddToCart: (item: CartItem) => void;
  cartQuantity: number;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  categoryId?: string;
}

export function MenuItemCard({
  item,
  onAddToCart,
  cartQuantity,
  onUpdateQuantity,
  categoryId,
}: MenuItemCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { restaurant } = useApp();
  const { isClosed, message } = useRestaurantStatus(restaurant);
  const handleAddToCart = () => {
    setIsModalOpen(true);
    // Push new history state so back button closes modal
    window.history.pushState({ modal: true }, "");
  };

  const handleCustomizedAdd = (customizedItem: CartItem) => {
    onAddToCart(customizedItem);
    setIsModalOpen(false);
    // Push new history state so back button closes modal
    window.history.pushState({ modal: true }, "");
  };

  // Stack history maintaine
  useEffect(() => {
    const handleBack = () => {
      if (isModalOpen) {
        setIsModalOpen(false);
        return;
      }
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [isModalOpen]);
  return (
    <>
      <Card
        className="group overflow-hidden bg-white border border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 rounded-2xl h-full flex flex-col animate-fade-in-up"
        data-category-id={String(categoryId)}
      >
        <CardContent className="p-0 flex flex-col h-full">
          {/* Image Section */}
          <div className="relative h-40 sm:h-48 overflow-hidden rounded-t-2xl">
            <img
              src={getMenuItemImageUrl(item.imageUrl)}
              alt={item.imageUrl}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-1 left-1 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
              <div className="flex items-center gap-1 bg-black/60 backdrop-blur-lg px-2.5 py-1 rounded-lg border border-white/10 shadow-md">
                <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
                <span className="text-[10px] sm:text-xs text-white font-medium tracking-wide">
                  Image for illustration only
                </span>
              </div>
            </div>
            {/* Top Badges */}
            <div className="absolute top-3 left-3">
              {item?.isFeatured && (
                <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md text-xs font-semibold px-2.5 py-1 rounded-full animate-bounce-gentle">
                  <Star className="w-3 h-3 mr-1 fill-current" />
                  Popular
                </Badge>
              )}
            </div>
            {/* Price Badge */}
            {/* <div className="absolute top-3 right-3">
              <Badge className="bg-white/95 backdrop-blur-sm text-emerald-700 font-bold text-sm shadow-md border border-white/30 px-2.5 py-1 rounded-full animate-pulse-slow">
                ${item?.basePrice?.toFixed(2)}
              </Badge>
            </div> */}
            <div className="absolute top-3 right-3 flex flex-row items-center space-x-2">
              {item.discountPrice < item.basePrice &&
              item.discountPrice != item.basePrice ? (
                <>
                  {/* Discounted Price */}
                  <Badge className="bg-white/95 backdrop-blur-sm text-emerald-700 font-bold text-sm shadow-md border border-white/30 px-2.5 py-1 rounded-full animate-pulse-slow">
                    ${item.discountPrice.toFixed(2)}
                  </Badge>
                  {/* Original Price (strikethrough) */}
                  <Badge className="bg-white/70 backdrop-blur-sm text-gray-500 text-xs font-medium shadow-md border border-white/30 px-2 py-0.5 rounded-full line-through">
                    ${item.basePrice.toFixed(2)}
                  </Badge>
                </>
              ) : (
                <Badge className="bg-white/95 backdrop-blur-sm text-emerald-700 font-bold text-sm shadow-md border border-white/30 px-2.5 py-1 rounded-full animate-pulse-slow">
                  ${item.basePrice.toFixed(2)}
                </Badge>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="p-4 sm:p-5 flex flex-col flex-1">
            {/* Title */}
            <h3 className="font-bold text-base sm:text-lg text-gray-900 leading-tight group-hover:text-emerald-700 transition-colors font-display mb-2 line-clamp-2 min-h-[1rem] sm:min-h-[1.5rem]">
              {item.name}
            </h3>
            {/* Category Badge */}
            <div className="flex items-center">
              <span className="px-2 py-0.5 mb-2 rounded-full bg-emerald-50 text-emerald-700 text-[10px] sm:text-xs font-medium border border-emerald-200 shadow-sm">
                {item?.categoryName}
              </span>
            </div>
            {/* Description */}
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed line-clamp-2 mb-3 sm:mb-4 min-h-[2rem] sm:min-h-[2.5rem]">
              {item.description}
            </p>

            {/* Badges Row */}
            {/* <div className="flex flex-wrap gap-1.5 mb-3 sm:mb-4 min-h-[1.5rem]">
              {item.isGlutenFree && (
                <Badge
                  variant="outline"
                  className="text-xs border-blue-200 text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md font-medium"
                >
                  GF
                </Badge>
              )}
              {item.isVegan && (
                <Badge
                  variant="outline"
                  className="text-xs text-green-700 border-green-200 bg-green-50 px-2 py-0.5 rounded-md font-medium"
                >
                  <Leaf className="w-3 h-3" />
                </Badge>
              )}
              {item.isSpicy && (
                <Badge
                  variant="outline"
                  className="text-xs text-red-700 border-red-200 bg-red-50 px-2 py-0.5 rounded-md font-medium animate-shake"
                >
                  <Flame className="w-3 h-3" />
                </Badge>
              )}
              {item.preparationTime && (
                <Badge
                  variant="outline"
                  className="text-xs text-gray-600 border-gray-200 bg-gray-50 px-2 py-0.5 rounded-md font-medium"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {item?.preparationTime}m
                </Badge>
              )}
            </div> */}

            {/* Action Section - Takes remaining space */}
            <div className="mt-auto space-y-3">
              <Button
                onClick={handleAddToCart}
                disabled={isClosed}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold h-10 sm:h-11 text-xs sm:text-sm rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              >
                <Plus className="w-4 h-4 mr-2" />
                Select
              </Button>

              {/* Cart Quantity Indicator */}
              {cartQuantity > 0 && (
                <div className="flex items-center justify-center space-x-3 animate-zoom-in">
                  {/* Minus Button */}
                  <Button
                    onClick={() => onUpdateQuantity(item.id, cartQuantity - 1)}
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 sm:h-10 sm:w-10 p-0 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
                  >
                    <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>

                  {/* Existing Badge – unchanged */}
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full animate-pulse-slow">
                    {cartQuantity} in cart
                  </Badge>

                  {/* Plus Button */}
                  <Button
                    onClick={() => onUpdateQuantity(item.id, cartQuantity + 1)}
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 sm:h-10 sm:w-10 p-0 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <FoodCustomizationModal
        item={item}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          // Remove the pushed state
          if (window.history.state?.modal) {
            window.history.back();
          }
        }}
        onAddToCart={handleCustomizedAdd}
      />
    </>
  );
}
